import { IncidentSchema } from '../chunk-6XJKVJAV.js';
import { normalizeAddress } from '../chunk-5JRVT5VM.js';
import '../chunk-43SJKO57.js';
import { toCanonicalChainId } from '../chunk-B3TPTPE2.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-PZ5AY32C.js';
import { z } from 'zod';

// src/incidents/notice.ts
function toneFor(notice) {
  if (!notice.references?.length) return "info";
  switch (notice.severity) {
    case "critical":
      return "critical";
    case "high":
    case "medium":
      return "warning";
    default:
      return "info";
  }
}
function isSubjectOfHarm(notice) {
  return notice.role === "victim" || notice.role === "recovery";
}
function chipLabelFor(notice) {
  const role = String(notice.role ?? "");
  const named = {
    exploiter: "Reported exploiter",
    intermediary: "Reported in exploit flow",
    cashout: "Reported cash-out",
    victim: "Exploit target",
    recovery: "Recovered funds"
  };
  return named[role] ?? "Named in an exploit report";
}
function bannerPropsFor(notice) {
  return {
    incidentId: notice.incident_id,
    tone: toneFor(notice),
    chipLabel: chipLabelFor(notice),
    headline: notice.headline ?? "",
    note: notice.note ?? "",
    summary: notice.summary ?? "",
    subjectOfHarm: isSubjectOfHarm(notice),
    references: notice.references ?? []
  };
}
function bannersFor(notices) {
  return (notices ?? []).filter((notice) => Boolean(notice?.headline)).map(bannerPropsFor);
}

// src/incidents/incident-catalog.generated.json
var incident_catalog_generated_default = {
  incidents: [
    {
      addresses: [
        {
          address: "0x9ae755d23fc948fe94c9364a2398fd508a2ab0d2",
          chain_id: "eip155:1",
          note: "Primary wallet. Bought NES on Ethereum, bridged to Nesa, inflated the balance via the Cosmos EVM vulnerability, bridged back and dispersed the proceeds through a wallet network.",
          role: "exploiter"
        },
        {
          address: "0x2816039940fd2ea2ecafce843cb2cc39e73f3fd6",
          chain_id: "eip155:1",
          note: "Received from the primary wallet and forwarded onward.",
          role: "intermediary"
        },
        {
          address: "0x83c85c3c045aedebd81a678a3ffd52794af6b414",
          chain_id: "eip155:1",
          note: "Received and forwarded large ETH amounts, including 27 ETH sent directly to the deposit feeder below.",
          role: "intermediary"
        },
        {
          address: "0xc810009a4f92ff10039a9ea9c705d2634a70446e",
          chain_id: "eip155:1",
          note: "The last non-custodial hop: gathered from several branches and sent repeated large ETH transfers into the KuCoin 17 hot wallet. Carries no exchange label of its own \u2014 its behaviour is consistent with either a per-user deposit address or an attacker-controlled feeder, and the public reporting does not settle which.",
          role: "cashout"
        }
      ],
      chains: [
        "eip155:1"
      ],
      id: "nesa_cosmos_evm_balance_inflation",
      name: "Nesa / Cosmos EVM balance inflation",
      occurred_on: "2026-09-04",
      references: [
        {
          handle: "@bubblemaps",
          label: "Bubblemaps",
          url: "https://x.com/bubblemaps/status/2092613254912852356"
        },
        {
          handle: "",
          label: "Cosmos security advisory GHSA-7g4w-cg88-2cq2",
          url: "https://github.com/cosmos/security/blob/main/communications/cosmos_evm_GHSA-7g4w-cg88-2cq2_post_mortem.md"
        }
      ],
      severity: "critical",
      summary: "A balance-inflation bug in Cosmos EVM let an attacker multiply a bridged NES position roughly 200x on Nesa Chain, bridge it back to Ethereum and sell it into the open market."
    },
    {
      addresses: [
        {
          address: "0x098b716b8aaf21512996dc57eb0615e2383e2f96",
          chain_id: "eip155:1",
          note: "Designated by OFAC on 2022-04-14, under the Lazarus Group entry, as the address that received the funds taken from the Ronin bridge.",
          role: "exploiter"
        },
        {
          address: "0x3cffd56b47b7b41c56258d9c7731abadc360e073",
          chain_id: "eip155:1",
          note: "Designated by OFAC on 2022-04-22, under the Lazarus Group entry, as one of three addresses that interacted with the drainer 0x098b\u20262f96 and held stolen ETH. On chain it received 10,129.935 ETH from the drainer on 2022-04-18 (tx 0xa04e54\u20266d29).",
          role: "intermediary"
        },
        {
          address: "0xa0e1c89ef1a489c9c7de96311ed5ce5d32c20e4b",
          chain_id: "eip155:1",
          note: "Designated by OFAC on 2022-04-22, under the Lazarus Group entry, as one of three addresses that interacted with the drainer 0x098b\u20262f96 and held stolen ETH. On chain it received 18,256.81 ETH from the drainer on 2022-04-19 (tx 0xa0b693\u2026230f).",
          role: "intermediary"
        },
        {
          address: "0x53b6936513e738f44fb50d2b9476730c0ab3bfc1",
          chain_id: "eip155:1",
          note: "Designated by OFAC on 2022-04-22, under the Lazarus Group entry, as one of three addresses that interacted with the drainer 0x098b\u20262f96 and held stolen ETH. On chain it received 21,629.48215 ETH from the drainer on 2022-04-21 (tx 0x899ad3\u202611d4).",
          role: "intermediary"
        }
      ],
      chains: [
        "eip155:1"
      ],
      id: "ronin_bridge_2022",
      name: "Ronin bridge",
      occurred_on: "2022-03-23",
      references: [
        {
          handle: "",
          label: "US Treasury (OFAC) SDN list",
          url: "https://home.treasury.gov/policy-issues/financial-sanctions/specially-designated-nationals-and-blocked-persons-list-sdn-human-readable-lists"
        },
        {
          handle: "",
          label: "Elliptic",
          url: "https://www.elliptic.co/blog/further-sanctions-against-north-koreas-lazarus-group-for-laundering-stolen-ronin-funds"
        },
        {
          handle: "",
          label: "CyberScoop",
          url: "https://cyberscoop.com/ronin-bridge-hack-lazarus-group-north-korea-treasury-sanctions/"
        }
      ],
      severity: "critical",
      summary: "The theft of 173,600 ETH and 25.5M USDC from the Ronin bridge behind Axie Infinity, which the US Treasury attributed to North Korea's Lazarus Group."
    }
  ]
};

// src/incidents/catalog.ts
var CatalogSchema = z.object({ incidents: z.array(IncidentSchema).default([]) });
var BUNDLED_INCIDENTS = Object.freeze(
  [...CatalogSchema.parse(incident_catalog_generated_default).incidents].sort((a, b) => {
    const left = a.occurred_on || "";
    const right = b.occurred_on || "";
    if (left === right) return a.id.localeCompare(b.id);
    if (!left) return 1;
    if (!right) return -1;
    return right.localeCompare(left);
  })
);
function bundledIncident(id) {
  return BUNDLED_INCIDENTS.find((incident) => incident.id === id);
}
function incidentAccounts(incident) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const entry of incident.addresses ?? []) {
    const address = String(entry.address ?? "").trim();
    const chain = String(entry.chain_id ?? "").trim();
    if (!address || !chain) continue;
    const account = `${toCanonicalChainId(chain) ?? chain}:${normalizeAddress(address, chain)}`;
    if (seen.has(account)) continue;
    seen.add(account);
    out.push(account);
  }
  return out;
}
function incidentRoleCounts(incident) {
  const counts = {};
  for (const entry of incident.addresses ?? []) {
    const role = String(entry.role ?? "") || "unknown";
    counts[role] = (counts[role] ?? 0) + 1;
  }
  return counts;
}

export { BUNDLED_INCIDENTS, bannerPropsFor, bannersFor, bundledIncident, chipLabelFor, incidentAccounts, incidentRoleCounts, isSubjectOfHarm, toneFor };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map